/* eslint-disable @typescript-eslint/no-explicit-any */
// Haven't been able to find any good typings for cell renderers

import { Icon, Intent, Tag, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    ColGroupDef,
    ICellRendererParams,
    IRowNode,
    RowClickedEvent,
    ValueGetterFunc
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
    useRegionConfig,
    useStageConfig
} from "in_queue/contexts/ClusterContext";
import { useAllQueueProjects } from "in_queue/contexts/ProjectDataContext";
import { InQueueRegionConfig } from "in_queue/types/configPerRegion";
import {
    ProjectType,
    SpeculativeProject,
    TrackedProject
} from "in_queue/types/projectType";
import {
    ScenarioMetrics,
    ScenarioMetricsMapping
} from "in_queue/types/scenarioMetricsType";
import {
    CostAssumptionType,
    ScenarioMetadata,
    ScenarioStage,
    ScenarioStatus
} from "in_queue/types/scenarioType";
import {
    applyHybridLimitToProject,
    isProjectExcluded,
    reduceHybridLimitByErisPercentage,
    reduceProjectSizeByPercentage
} from "in_queue/types/scenarioUtils";
import { formatPower, formatRelativeDate } from "in_queue/utils/formatting";
import { formatRank } from "in_queue/utils/rankFormatting";
import {
    getCostAssumptionText,
    shouldShowCostAssumptions
} from "in_queue/utils/scenarios";
import {
    ScenarioSelectionMode,
    ScenarioSelectionRules,
    scenarioSelectionRules
} from "in_queue/utils/scenarioSelection";

import { CustomUpgradeDialog } from "in_queue/components/CustomUpgrades/CustomUpgradeDialog";
import {
    CustomUpgrade,
    useCustomUpgrades
} from "in_queue/contexts/CustomUpgradesContext";
import { useCustomUpgradesStatus } from "in_queue/hooks/useCustomUpgradesStatus";
import { useScenarioTableSelection } from "in_queue/hooks/useScenarioTableSelection";
import { MutableRefObject, RefObject, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loading } from "types/loadingType";
import { StaleScenarioTag } from "../common/StaleScenarioTag";
import { TextLinkButton } from "../common/TextLinkButton";
import {
    AgGridColumnDefs,
    cellTooltipValueGetter,
    currencyFormatter,
    currencyFormatterHelper,
    hideUnwantedColumns,
    NiraColDef,
    roundingFormatter,
    ScenariosTableColumnId,
    stringFormatter,
    timingFormatter
} from "./agGrid/agGridHelpers";
import { BaseAgGrid } from "./base/BaseAgGrid";
import { RankCell } from "./RankPill";
import { scenarioStatusPostSort, SCENARIO_ROW_GROUP } from "./scenarioRowGroup";
import scenariosCss from "./ScenariosTable.module.scss";
import { SensitivitiesSummary } from "./SensitivitiesSummary";
import css from "./TableStyles.module.scss";

const ScenarioCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata>
) => {
    const scenario = params.data;
    const scenarioId = scenario?.scenarioId;
    return (
        <span className={css["scenario-title-cell"]}>
            <Link
                to={`scenario/${scenarioId}`}
                className={`${css["link"]} ${css["scenario-title-text"]}`}
            >
                {scenario?.title}
            </Link>
            {scenario?.archived && (
                <Tag
                    minimal
                    round
                    intent={Intent.WARNING}
                    className={css["scenario-title-tag"]}
                >
                    Archived
                </Tag>
            )}
        </span>
    );
};

// Renders the cost cell with an inline info-icon when the scenario fully
// drops the current project (eris === 100 && nris === 100). The row
// already shows a $0 cost; the icon clarifies that's because the project
// is dropped, not because the scenario has no allocation. Asymmetric
// reductions (e.g. eris=100 only) still produce real cost results, so we
// use the canonical `isProjectExcluded` predicate.
const CostCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata> & {
        projectId?: string;
    }
) => {
    const ownAssumption = params.projectId
        ? params.data?.assumptions?.[params.projectId]
        : undefined;
    const scenarioFailed = params.data?.status === ScenarioStatus.RunFailed;
    const droppedHere = isProjectExcluded(ownAssumption) && !scenarioFailed;
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span>{currencyFormatterHelper(params.value)}</span>
            {droppedHere && (
                <Tooltip content="This scenario dropped this project." compact>
                    <Icon
                        icon={IconNames.INFO_SIGN}
                        intent={Intent.PRIMARY}
                        size={14}
                    />
                </Tooltip>
            )}
        </span>
    );
};

const NoneCell = () => <span className={css["muted-cell"]}>None</span>;

interface ScenariosGridContext {
    projectIdToErisMw: Record<string, number> | null;
    clusterMw: number;
    setOpenCustomUpgrade: (customUpgrade: CustomUpgrade | null) => void;
}

const SensitivitiesCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata> & {
        context: ScenariosGridContext;
    }
) => {
    const scenario = params.data;

    if (!scenario) {
        return <NoneCell />;
    }

    if (Object.keys(params.value).length === 0) {
        return <NoneCell />;
    }

    const speculativeProjectIds = scenario.projectAdditions.map(
        (pa) => pa.projectId
    );

    // Speculative Projects (Project Additions) are shown in a separate column
    const sensitivitiesExcludingSpeculative = Object.keys(params.value).filter(
        (projectId) => !speculativeProjectIds.includes(projectId)
    );
    if (sensitivitiesExcludingSpeculative.length === 0) {
        return <NoneCell />;
    }

    const { projectIdToErisMw, clusterMw } = params.context;
    if (!projectIdToErisMw || clusterMw === 0) {
        return <NoneCell />;
    }

    let mwRemoved = 0;
    for (const [projectId, assumption] of Object.entries(
        scenario.assumptions
    )) {
        if (!assumption) continue;
        const erisMw = projectIdToErisMw[projectId];
        if (erisMw != null) {
            mwRemoved += erisMw * (assumption.eris / 100);
        }
    }
    return <SensitivitiesSummary mwRemoved={mwRemoved} clusterMw={clusterMw} />;
};

const ProjectAdditionsCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata>
) => {
    const scenario = params.data;
    if (!scenario) {
        return <NoneCell />;
    }

    if (params.value.length === 0) {
        return <NoneCell />;
    }

    const mwAdded = scenario.projectAdditions.reduce(
        (sum: number, sp: SpeculativeProject) => {
            const erisReductionPct =
                scenario.assumptions[sp.projectId]?.eris ?? 0;
            return sum + sp.size.erisMw * (1 - erisReductionPct / 100);
        },
        0
    );

    if (mwAdded === 0) {
        return <NoneCell />;
    }

    const projectCount = scenario.projectAdditions.length;
    return (
        <div className={css["two-line-cell"]}>
            <span>Adds {formatPower(mwAdded)}</span>
            <span className={css["muted-cell"]}>
                {projectCount} {projectCount === 1 ? "project" : "projects"}
            </span>
        </div>
    );
};

const COST_ASSUMPTION_TAG: Record<
    CostAssumptionType,
    { label: string; intent: Intent }
> = {
    [CostAssumptionType.CONSERVATIVE]: {
        label: "Conservative",
        intent: Intent.WARNING
    },
    [CostAssumptionType.CUSTOM]: {
        label: "Custom",
        intent: Intent.PRIMARY
    },
    [CostAssumptionType.NETWORK_UPGRADES]: {
        label: "ISO-Provided",
        intent: Intent.SUCCESS
    },
    [CostAssumptionType.SUGAR_UPGRADES]: {
        label: "MISO Whitepaper Estimates",
        intent: Intent.NONE
    }
};

const CostAssumptionsCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata>
) => {
    const type = params.data?.costAssumptions.type;
    if (!type) {
        return <NoneCell />;
    }
    const { label, intent } = COST_ASSUMPTION_TAG[type];
    return (
        <Tag minimal intent={intent}>
            {label}
        </Tag>
    );
};

const CustomUpgradesCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata> & {
        context: ScenariosGridContext;
    }
) => {
    const customUpgrade: CustomUpgrade | undefined = params.value;
    if (!customUpgrade) {
        return <NoneCell />;
    }

    const onClick = () => {
        params.context.setOpenCustomUpgrade(customUpgrade);
    };
    return <TextLinkButton text={customUpgrade.name} onClick={onClick} />;
};

const STATUS_DOT_CLASS: Record<string, string> = {
    Done: "status-dot-done",
    Failed: "status-dot-failed",
    Draft: "status-dot-draft",
    Queued: "status-dot-queued",
    Running: "status-dot-running"
};

const ScenarioRunCellRenderer: any = (
    params: ICellRendererParams<ScenarioMetadata>
) => {
    const scenario = params.data;
    if (!scenario) {
        return "-";
    }

    const timing = scenario.timeMetadata;
    let statusText = "-";
    let timingDate: Date | undefined;

    if (scenario.status === ScenarioStatus.Draft) {
        statusText = "Draft";
        timingDate = timing.createdAt;
    } else if (
        scenario.status === ScenarioStatus.RunRequested ||
        scenario.status === ScenarioStatus.RunSubmitted
    ) {
        statusText = "Queued";
        timingDate = timing.runSubmittedAt ?? timing.createdAt;
    } else if (scenario.status === ScenarioStatus.Running) {
        statusText = "Running";
        timingDate = timing.runStartedAt ?? timing.runSubmittedAt;
    } else if (
        scenario.status === ScenarioStatus.RunSucceeded ||
        scenario.archived
    ) {
        statusText = "Done";
        timingDate = timing.runCompletedAt;
    } else if (scenario.status === ScenarioStatus.RunFailed) {
        statusText = "Failed";
        timingDate = timing.runCompletedAt ?? timing.runStartedAt;
    }

    const dotClass = STATUS_DOT_CLASS[statusText];
    const dot = dotClass ? (
        <span className={`${css["status-dot"]} ${css[dotClass]}`} />
    ) : null;

    const staleTag = scenario.isStale ? <StaleScenarioTag /> : null;

    const statusLine = (
        <span>
            {dot} {statusText} {staleTag}
        </span>
    );

    if (!timingDate) {
        return statusLine;
    }

    return (
        <div className={css["two-line-cell"]}>
            {statusLine}
            <span className={css["muted-cell"]}>
                {formatRelativeDate(timingDate)}
            </span>
        </div>
    );
};

// The Status column orders first by staleness and then by time completed at.
const staleThenTimingComparator = (
    valueA: Date | undefined,
    valueB: Date | undefined,
    nodeA: IRowNode<ScenarioMetadata>,
    nodeB: IRowNode<ScenarioMetadata>
): number => {
    const staleA = nodeA.data?.isStale === true;
    const staleB = nodeB.data?.isStale === true;
    if (staleA !== staleB) {
        return staleA ? 1 : -1;
    }
    return (valueB?.getTime() ?? 0) - (valueA?.getTime() ?? 0);
};

const getColumnDefs = ({
    project,
    scenarioMetrics,
    customUpgrades,
    config,
    showCostAssumptions,
    showCustomUpgrades,
    showSpeculativeProjects,
    rules
}: {
    project: Loading<TrackedProject | undefined>;
    scenarioMetrics: Loading<ScenarioMetricsMapping>;
    customUpgrades: Loading<CustomUpgrade[]>;
    config: InQueueRegionConfig;
    showCostAssumptions: boolean;
    showCustomUpgrades: boolean;
    showSpeculativeProjects: boolean;
    rules: ScenarioSelectionRules | undefined;
}): AgGridColumnDefs<ScenarioMetadata, ScenariosTableColumnId> => {
    const projectId =
        project === "loading" || project === undefined
            ? undefined
            : project.projectId;

    const assumptionsChildren = [...getProjectSizeColumns(project, config)];

    assumptionsChildren.push({
        colId: "sensitivities",
        field: "assumptions",
        headerName: "Sensitivities",
        minWidth: 170,
        valueFormatter: (params) => Object.keys(params.value).join(", "),
        cellRenderer: SensitivitiesCellRenderer,
        flex: 1
    });

    if (showSpeculativeProjects) {
        assumptionsChildren.push({
            colId: "projectAdditions",
            field: "projectAdditions",
            headerName: "Project Additions",
            minWidth: 170,
            cellRenderer: ProjectAdditionsCellRenderer,
            flex: 1
        });
    }

    if (showCostAssumptions) {
        assumptionsChildren.push({
            colId: "costAssumptions",
            headerName: "Cost assumptions",
            width: 200,
            valueGetter: ({ data }) => getCostAssumptionText(data),
            cellRenderer: CostAssumptionsCellRenderer
        });
    }

    if (showCustomUpgrades) {
        assumptionsChildren.push({
            colId: "customUpgrades",
            headerName: "Custom upgrades",
            width: 200,
            valueGetter: ({ data }) => {
                const customUpgradeId = data?.customUpgradeId;
                if (customUpgrades === "loading" || !customUpgradeId) {
                    return undefined;
                }

                return customUpgrades.find(
                    (upgrade) => upgrade.id === customUpgradeId
                );
            },
            cellRenderer: CustomUpgradesCellRenderer
        });
    }

    const columnDefs: AgGridColumnDefs<
        ScenarioMetadata,
        ScenariosTableColumnId
    > = [
        {
            colId: "title",
            field: "title",
            headerName: "Scenario",
            minWidth: 300,
            cellRenderer: ScenarioCellRenderer,
            checkboxSelection: rules !== undefined,
            showDisabledCheckboxes: rules !== undefined,
            tooltipValueGetter: (params) =>
                rules !== undefined &&
                params.data !== undefined &&
                !rules.isRowSelectable(params.data)
                    ? rules.disabledReason(params.data)
                    : cellTooltipValueGetter(params),
            flex: 2,
            pinned: true
        },
        getMetricsColumns(scenarioMetrics, projectId),
        {
            headerName: "Assumptions",
            children: assumptionsChildren
        },
        {
            colId: "runStatus",
            field: "timeMetadata.latestDate",
            headerName: "Status",
            // Wider than the text needs so the "Stale" tag fits beside it.
            width: 175,
            cellRenderer: ScenarioRunCellRenderer,
            valueFormatter: timingFormatter,
            comparator: staleThenTimingComparator,
            enableCellChangeFlash: true
        },
        {
            colId: "inputData",
            headerName: "Input data",
            field: "inputDataUsed.concise",
            width: 300,
            valueFormatter: stringFormatter
        }
    ];

    const columnsToRemove = [...config.scenariosTable.columnsToRemove];
    hideUnwantedColumns(columnDefs, columnsToRemove);

    return columnDefs;
};

const getProjectSizeColumns = (
    project: Loading<TrackedProject | undefined>,
    config: InQueueRegionConfig
): NiraColDef<ScenarioMetadata, ScenariosTableColumnId>[] => {
    const getModifiedSize = (data: ScenarioMetadata | undefined) => {
        if (project === "loading" || project === undefined || !data) {
            return undefined;
        }
        // Only queue projects have metadata and can use applyHybridLimitToProject
        const hybridAppliedSize =
            project.type === ProjectType.QUEUE_PROJECT
                ? applyHybridLimitToProject(project)
                : project.size;
        if (!(project.projectId in data.assumptions)) {
            return hybridAppliedSize;
        }
        return reduceProjectSizeByPercentage(
            hybridAppliedSize,
            data.assumptions[project.projectId]
        );
    };
    const formatSize = (value: number) =>
        value.toLocaleString(undefined, {
            useGrouping: false,
            maximumFractionDigits: 2
        });
    const SizeWithReductionRenderer =
        (field: "erisMw" | "nrisMw"): any =>
        (params: ICellRendererParams<ScenarioMetadata>) => {
            const after = getModifiedSize(params.data)?.[field];
            if (after === undefined) {
                return <span>-</span>;
            }
            const assumption =
                project !== "loading" && project && params.data
                    ? params.data.assumptions[project.projectId]
                    : undefined;
            const reductionPct =
                assumption?.[field === "erisMw" ? "eris" : "nris"] ?? 0;
            if (reductionPct === 0) {
                return <span>{formatSize(after)} MW</span>;
            }
            return (
                <span>
                    {formatSize(after)} MW{" "}
                    <span className={css["muted-cell"]}>
                        (↓ {reductionPct}%)
                    </span>
                </span>
            );
        };
    const getHybridLimit = (data: ScenarioMetadata | undefined) => {
        if (project === "loading" || project === undefined || !data) {
            return undefined;
        }
        // Only queue projects have metadata, return null for speculative projects
        if (project.type !== ProjectType.QUEUE_PROJECT) {
            return null;
        }
        const baseHybridLimit = +(project.metadata?.["Hybrid Limit"] ?? 0);
        if (baseHybridLimit === 0) {
            return "-";
        }
        // If the scenario applies a percentage reduction to this project,
        // scale the Hybrid Limit by the ERIS reduction. This matches what the
        // cost-allocation pipeline does with `project_mw_size` for hybrid
        // projects (see reduceHybridLimitByErisPercentage).
        const assumption = data.assumptions[project.projectId];
        if (!assumption) {
            return baseHybridLimit;
        }
        return reduceHybridLimitByErisPercentage(baseHybridLimit, assumption);
    };
    return [
        {
            colId: "energyMw",
            headerName: config.sizeLabel.energyLabel,
            type: "numericColumn",
            width: 140,
            valueGetter: ({ data }) => getModifiedSize(data)?.erisMw ?? "-",
            valueFormatter: roundingFormatter,
            cellRenderer: SizeWithReductionRenderer("erisMw")
        },
        {
            colId: "capacityMw",
            headerName: config.sizeLabel.capacityLabel,
            type: "numericColumn",
            width: 140,
            valueGetter: ({ data }) => getModifiedSize(data)?.nrisMw ?? "-",
            valueFormatter: roundingFormatter,
            cellRenderer: SizeWithReductionRenderer("nrisMw")
        },
        {
            colId: "hybridLimit",
            headerName: "Hybrid Limit",
            type: "numericColumn",
            headerTooltip:
                "SPP's published POI cap for this hybrid project. In scenarios with a percentage downsize, this value is scaled by the scenario's ERIS reduction to reflect the effective POI injection used in cost allocation (matches `mw_impact = hybrid_limit × (1 − ERIS reduction) × DFAX`).",
            width: 80,
            valueGetter: ({ data }) => getHybridLimit(data),
            valueFormatter: roundingFormatter
        }
    ];
};

const getMetricsColumns = (
    scenarioMetrics: Loading<ScenarioMetricsMapping>,
    projectId: string | undefined
): ColGroupDef<ScenarioMetadata> => {
    const createMetricsValueGetter = (
        field: keyof ScenarioMetrics
    ): ValueGetterFunc<ScenarioMetadata> => {
        return ({ data }) => {
            if (scenarioMetrics === "loading" || !data?.scenarioId) {
                return undefined;
            }
            return scenarioMetrics[data.scenarioId]?.[field];
        };
    };

    const rankColumn: NiraColDef<ScenarioMetadata, ScenariosTableColumnId> = {
        colId: "rank",
        headerName: "Rank",
        headerTooltip:
            "Project rank by $/MW within all projects in the scenario (1 = cheapest). Percentile = rank / total.",
        type: "numericColumn",
        width: 90,
        cellRenderer: ({ data }: ICellRendererParams<ScenarioMetadata>) => {
            const entry =
                data?.scenarioId !== undefined && scenarioMetrics !== "loading"
                    ? scenarioMetrics[data.scenarioId]
                    : undefined;
            return (
                <RankCell
                    rank={entry?.rank}
                    totalRanked={entry?.totalRanked}
                    percentile={entry?.rankPercentile}
                />
            );
        },
        useValueFormatterForExport: true,
        valueGetter: ({ data }) =>
            (scenarioMetrics !== "loading" &&
                data?.scenarioId &&
                scenarioMetrics[data.scenarioId]?.rankPercentile) ??
            null,
        valueFormatter: ({
            data,
            value
        }: {
            data?: ScenarioMetadata;
            value: number | null;
        }) => {
            if (
                value === null ||
                value === undefined ||
                !data?.scenarioId ||
                scenarioMetrics === "loading"
            ) {
                return "-";
            }
            const entry = scenarioMetrics[data.scenarioId];
            if (entry?.rank === undefined || !entry.totalRanked) {
                return `${value}`;
            }
            return formatRank(entry.rank, entry.totalRanked, value);
        },
        comparator: (a, b, nodeA, nodeB) => {
            if (a === null && b === null) return 0;
            if (a === null) return 1;
            if (b === null) return -1;
            if (a !== b) return a - b;
            const sidA = nodeA.data?.scenarioId;
            const sidB = nodeB.data?.scenarioId;
            if (scenarioMetrics === "loading") return 0;
            const rankA = sidA ? scenarioMetrics[sidA]?.rank ?? 0 : 0;
            const rankB = sidB ? scenarioMetrics[sidB]?.rank ?? 0 : 0;
            return rankA - rankB;
        }
    };

    return {
        headerName: "Results",
        children: [
            {
                colId: "cost",
                headerName: "Cost",
                type: "numericColumn",
                width: 110,
                // Export the raw number, not the compact "$109K"/"250M"
                // string, so the CSV is numeric in Excel.
                useValueFormatterForExport: false,
                cellRenderer: CostCellRenderer,
                cellRendererParams: { projectId },
                valueFormatter: currencyFormatter,
                valueGetter: createMetricsValueGetter("sumAllocatedCost")
            },
            {
                colId: "costPerMw",
                headerName: "$ / MW",
                type: "numericColumn",
                width: 90,
                useValueFormatterForExport: false,
                valueFormatter: currencyFormatter,
                valueGetter: createMetricsValueGetter("costPerMw")
            },
            rankColumn
        ]
    };
};

export const ScenariosTable: React.FC<{
    project: Loading<TrackedProject | undefined>;
    scenarios: Loading<ScenarioMetadata[]>;
    scenarioMetrics: Loading<ScenarioMetricsMapping>;
    height?: "auto" | number;
    gridRef: RefObject<AgGridReact>;
    wrapperRef?: MutableRefObject<HTMLDivElement | null>;
    scenarioStage: ScenarioStage;
    selectionMode?: ScenarioSelectionMode;
    onSelectedScenariosChange?: (
        selected: ReadonlyArray<ScenarioMetadata>
    ) => void;
}> = ({
    project,
    scenarios,
    scenarioMetrics,
    height,
    gridRef,
    wrapperRef,
    scenarioStage,
    selectionMode,
    onSelectedScenariosChange
}) => {
    const navigate = useNavigate();
    const config = useRegionConfig();
    const stageConfig = useStageConfig();

    const clusterProjects = useAllQueueProjects();
    const customUpgrades = useCustomUpgrades();
    const rules = useMemo(
        () =>
            selectionMode === undefined
                ? undefined
                : scenarioSelectionRules(selectionMode),
        [selectionMode]
    );
    const onSelectionChanged = useScenarioTableSelection({
        mode: selectionMode,
        gridRef,
        onSelectedScenariosChange
    });
    const isSelecting = rules !== undefined;
    const [openCustomUpgrade, setOpenCustomUpgrade] =
        useState<CustomUpgrade | null>(null);

    const showCostAssumptions = shouldShowCostAssumptions({
        stage: scenarioStage,
        supportedCostAssumptionTypes: stageConfig.supportedCostAssumptionTypes
    });

    // Secondary surface: only show the column when fully enabled (treat
    // `locked` as hidden).
    const showCustomUpgrades = useCustomUpgradesStatus() === "enabled";

    const gridContext = useMemo<ScenariosGridContext>(() => {
        if (clusterProjects === "loading") {
            return {
                projectIdToErisMw: null,
                clusterMw: 0,
                setOpenCustomUpgrade
            };
        }
        const projectIdToErisMw: Record<string, number> = {};
        let clusterMw = 0;
        for (const project of clusterProjects) {
            clusterMw += project.size.erisMw;
            projectIdToErisMw[project.projectId] = project.size.erisMw;
        }
        return { projectIdToErisMw, clusterMw, setOpenCustomUpgrade };
    }, [clusterProjects]);

    const navigateClick = (event: RowClickedEvent<ScenarioMetadata>) => {
        const scenarioId = event.data?.scenarioId;
        if (scenarioId) {
            navigate(`scenario/${scenarioId}`);
        }
    };

    return (
        <>
            <div className={scenariosCss["ScenariosTable-wrapper"]}>
                <BaseAgGrid<ScenarioMetadata>
                    ref={gridRef}
                    wrapperRef={wrapperRef}
                    loadingData={scenarios}
                    analyticsTableName="Scenarios"
                    context={gridContext}
                    stickyHeader={height === undefined}
                    defaultColDef={{
                        cellStyle: {
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden"
                        }
                    }}
                    columnDefs={getColumnDefs({
                        project,
                        scenarioMetrics,
                        customUpgrades,
                        config,
                        showCostAssumptions,
                        showSpeculativeProjects:
                            stageConfig.enableSpeculativeProjects,
                        showCustomUpgrades,
                        rules
                    })}
                    rowGroup={SCENARIO_ROW_GROUP}
                    postSortRows={scenarioStatusPostSort}
                    getRowId={(params) => params.data.scenarioId}
                    height={height}
                    onCellClicked={isSelecting ? undefined : navigateClick}
                    onSelectionChanged={onSelectionChanged}
                    rowSelection="multiple"
                    rowMultiSelectWithClick={isSelecting}
                    suppressRowClickSelection={!isSelecting}
                    isRowSelectable={(node) =>
                        node.data !== undefined &&
                        (rules?.isRowSelectable(node.data) ?? false)
                    }
                    rowHeight={56}
                    selectableRows
                />
            </div>
            {openCustomUpgrade && (
                <CustomUpgradeDialog
                    isOpen
                    upgrade={openCustomUpgrade}
                    onClose={() => setOpenCustomUpgrade(null)}
                    showArchiveButton={false}
                />
            )}
        </>
    );
};
